// Shared AudioContext — created and unlocked on first user gesture so that
// sounds triggered from WebSocket callbacks (non-user-gesture) can play.
let _ctx = null

export function unlockAudio() {
  try {
    if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)()
    if (_ctx.state === 'suspended') _ctx.resume()
  } catch {}
}

export const SOUNDS = [
  {
    id: 'chime',
    label: 'Chime',
    description: 'Two-note ascending bell',
  },
  {
    id: 'ding',
    label: 'Ding',
    description: 'Single clean bell strike',
  },
  {
    id: 'soft',
    label: 'Soft',
    description: 'Gentle low tone',
  },
  {
    id: 'pop',
    label: 'Pop',
    description: 'Quick pluck',
  },
  {
    id: 'alert',
    label: 'Alert',
    description: 'Three-note attention chime',
  },
  {
    id: 'none',
    label: 'None',
    description: 'Silent',
  },
]

export function playSound(soundId, volume = 80) {
  if (soundId === 'none') return
  // Only play if the context has been unlocked by a prior user gesture
  if (!_ctx || _ctx.state !== 'running') return
  _schedule(_ctx, soundId, Math.max(0, Math.min(100, volume)) / 100)
}

function _schedule(ctx, soundId, vol) {
  try {
    function bell(freq, startAt, masterVol) {
      const partials = [
        { ratio: 1,     amp: 1.00, decay: 1.4  },
        { ratio: 2.756, amp: 0.40, decay: 0.9  },
        { ratio: 5.404, amp: 0.20, decay: 0.6  },
        { ratio: 8.933, amp: 0.08, decay: 0.35 },
      ]
      partials.forEach(({ ratio, amp, decay }) => {
        const osc  = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.value = freq * ratio
        gain.gain.setValueAtTime(0, startAt)
        gain.gain.linearRampToValueAtTime(masterVol * amp, startAt + 0.006)
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + decay)
        osc.start(startAt)
        osc.stop(startAt + decay)
      })
    }

    function tone(freq, startAt, duration, masterVol, type = 'sine') {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = type
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0, startAt)
      gain.gain.linearRampToValueAtTime(masterVol, startAt + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)
      osc.start(startAt)
      osc.stop(startAt + duration)
    }

    const t = ctx.currentTime

    if (soundId === 'chime') {
      // E5 → B5 ascending two-note bell
      bell(659.25, t,        vol * 0.75)
      bell(987.77, t + 0.22, vol * 0.75)
    } else if (soundId === 'ding') {
      // Single clean A5 bell strike
      bell(880, t, vol * 0.75)

    } else if (soundId === 'soft') {
      // Mellow low C4 with long gentle decay
      tone(261.63, t, 1.2, vol * 0.35, 'sine')
      tone(523.25, t, 0.6, vol * 0.10, 'sine')

    } else if (soundId === 'pop') {
      // Short pluck — higher freq decays fast
      tone(1200, t,        0.08, vol * 0.5)
      tone(800,  t + 0.04, 0.12, vol * 0.3)

    } else if (soundId === 'alert') {
      // Three ascending notes: C5 → E5 → G5
      bell(523.25, t,        vol * 0.6)
      bell(659.25, t + 0.15, vol * 0.6)
      bell(783.99, t + 0.30, vol * 0.6)
    }
  } catch {} // silently ignore if AudioContext unavailable
}
